import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { AuthUser, UserRole, Permission, User } from '../types';
import apiService from '../services/api';

interface AuthContextType {
  currentUser: AuthUser | null;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  permissions: Permission;
  hasPermission: (permission: keyof Permission) => boolean;
  isAuthenticated: boolean;
  isLoading: boolean;
  users: User[];
  addUser: (user: Omit<User, 'id' | 'createdDate'>) => Promise<void>;
  updateUser: (id: string, updates: Partial<User>) => Promise<void>;
  deleteUser: (id: string) => Promise<void>;
  updateUserEmail: (id: string, email: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const getPermissionsForRole = (role: UserRole): Permission => {
  switch (role) {
    case 'Administrator':
      return {
        canViewAllMenus: true,
        canManageUsers: true,
        canRunAPRAnalysis: true,
        canViewAPRScoring: true,
        canModifyApplications: true,
        canViewDashboard: true,
        canManageSettings: true,
      };
    case 'APR Analyst':
      return {
        canViewAllMenus: true,
        canManageUsers: false,
        canRunAPRAnalysis: true,
        canViewAPRScoring: true,
        canModifyApplications: true,
        canViewDashboard: true,
        canManageSettings: true,
      };
    case 'Application Owner':
      return {
        canViewAllMenus: false,
        canManageUsers: false,
        canRunAPRAnalysis: false,
        canViewAPRScoring: true,
        canModifyApplications: false,
        canViewDashboard: false,
        canManageSettings: false,
      };
    default:
      return {
        canViewAllMenus: false,
        canManageUsers: false,
        canRunAPRAnalysis: false,
        canViewAPRScoring: false,
        canModifyApplications: false,
        canViewDashboard: false,
        canManageSettings: false,
      };
  }
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<AuthUser | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const permissions = currentUser ? getPermissionsForRole(currentUser.role) : getPermissionsForRole('Application Owner');

  // Load user from localStorage on mount and fetch users
  useEffect(() => {
    const savedUser = localStorage.getItem('user');
    const token = localStorage.getItem('authToken');

    if (savedUser && token) {
      try {
        const user = JSON.parse(savedUser);
        setCurrentUser(user);

        // Fetch users from API
        apiService.getUsers().then(setUsers).catch(() => {}).finally(() => setIsLoading(false));
      } catch (error) {
        localStorage.removeItem('user');
        localStorage.removeItem('authToken');
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }
  }, []);

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      setIsLoading(true);
      const response = await apiService.login(email, password);

      // Check if user must change password on first login
      if (response.mustChangePassword) {
        setIsLoading(false);
        const error: any = new Error('MUST_CHANGE_PASSWORD');
        error.mustChangePassword = true;
        error.email = email;
        throw error;
      }

      setCurrentUser(response.user);

      // Fetch users after login
      const usersData = await apiService.getUsers();
      setUsers(usersData);

      setIsLoading(false);
      return true;
    } catch (error) {
      setIsLoading(false);
      // Re-throw the error so the Login component can handle it
      throw error;
    }
  };

  const logout = async () => {
    await apiService.logout();
    setCurrentUser(null);
    setUsers([]);
  };

  const hasPermission = (permission: keyof Permission): boolean => {
    return permissions[permission];
  };

  const addUser = async (userData: Omit<User, 'id' | 'createdDate'>) => {
    try {
      const newUser = await apiService.createUser(userData);
      setUsers(prev => [...prev, newUser]);
    } catch (error) {
      throw error;
    }
  };

  const updateUser = async (id: string, updates: Partial<User>) => {
    try {
      const updatedUser = await apiService.updateUser(id, updates);
      setUsers(prev => prev.map(user => 
        user.id === id ? updatedUser : user
      ));
    } catch (error) {
      throw error;
    }
  };

  const deleteUser = async (id: string) => {
    try {
      await apiService.deleteUser(id);
      setUsers(prev => prev.filter(user => user.id !== id));
    } catch (error) {
      throw error;
    }
  };

  const updateUserEmail = async (id: string, email: string) => {
    try {
      const updatedUser = await apiService.updateUser(id, { email });
      setUsers(prev => prev.map(user => 
        user.id === id ? updatedUser : user
      ));
    } catch (error) {
      throw error;
    }
  };

  const value: AuthContextType = {
    currentUser,
    login,
    logout,
    permissions,
    hasPermission,
    isAuthenticated: !!currentUser,
    isLoading,
    users,
    addUser,
    updateUser,
    deleteUser,
    updateUserEmail
  };

  // Always render children - don't unmount during loading
  // The Login component handles its own loading state
  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};